import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { BrandMark } from "@/components/BrandMark";
import { useSEO } from "@/lib/useSEO";
import { formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  useSEO({
    title: "Reset Password — The Flow State Method",
    description: "Reset your Better Through Boxing password.",
    path: "/forgot-password",
  });
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      // Server always returns 200 — but show a generic toast on network errors
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send reset email — try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#050505] p-6 text-white" data-testid="forgot-password-page">
      <div className="w-full max-w-md border border-[#27272A] bg-[#0A0A0A] rounded-sm p-8">
        <Link to="/" className="inline-block mb-6" data-testid="forgot-logo"><BrandMark size="md" /></Link>
        {sent ? (
          <div data-testid="forgot-sent">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">Check your email</p>
            <h1 className="font-display text-3xl uppercase tracking-tight mb-3">Reset link sent.</h1>
            <p className="text-sm text-zinc-400 leading-relaxed mb-5">
              If an account exists for <strong className="text-white">{email}</strong>, you&apos;ll receive an email with a reset link in the next minute. The link expires in 1 hour.
            </p>
            <p className="text-xs text-zinc-500 leading-relaxed mb-5">
              Didn&apos;t see it? Check spam / Promotions / Updates. Make sure the email matches the account.
            </p>
            <Link to="/login" data-testid="forgot-back-to-login">
              <Button variant="outline" className="w-full border-[#27272A] bg-[#0F0F0F] text-zinc-300 hover:text-white uppercase tracking-widest font-bold text-xs">
                <ArrowLeft className="h-3 w-3 mr-2" /> Back to sign in
              </Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} data-testid="forgot-form">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-2">Forgot password</p>
            <h1 className="font-display text-3xl uppercase tracking-tight mb-3">Reset it.</h1>
            <p className="text-sm text-zinc-400 leading-relaxed mb-6">
              Enter the email you signed up with. We&apos;ll send you a single-use link to pick a new password.
            </p>

            <div className="space-y-2 mb-5">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Email</Label>
              <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0F0F0F] border-[#27272A] text-white rounded-sm h-12" data-testid="forgot-email" />
            </div>

            <Button type="submit" disabled={busy} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm py-6 disabled:opacity-50" data-testid="forgot-submit">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : <><Mail className="h-4 w-4 mr-2" /> Send reset link</>}
            </Button>

            <p className="text-sm text-zinc-500 text-center mt-5">
              Remembered it? <Link to="/login" className="text-[#FF331F] hover:underline" data-testid="forgot-login-link">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
